import * as graphics from "@client/app/graphics";
import * as dialogs from "@client/features/dialogs";
import * as orm from "@client/features/orm";
import * as forms from "@client/forms";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {CheckIcon, RocketLaunchIcon} from "@heroicons/react/24/outline";
import {server} from "@reactivated";
import {produce} from "immer";
import React from "react";

const Queued = () => {
    return (
        <div className="mt-4">
            <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-green-100">
                <RocketLaunchIcon
                    className="size-8 text-green-600"
                    aria-hidden="true"
                />
            </div>
            <div className="mt-3 text-center sm:mt-5">
                <h3 className="mt-2 text-xl/6 text-gray-900">
                    Your data migration is queued
                </h3>
                <div className="mt-3">
                    <p className="text-sm text-gray-500">
                        We’ll email you when it’s done.
                    </p>
                    <p className="mt-3 text-sm text-gray-500">
                        Until then, <span className="font-semibold">do not update</span>{" "}
                        your MyFitnessPal credentials.
                    </p>
                    <p className="mt-4 text-sm text-gray-500">
                        Make sure you’re receiving our emails from{" "}
                        <a href="mailto:support@joyapp.com" className="font-semibold">
                            support@joyapp.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

const Migrated = () => {
    return (
        <div className="mt-4">
            <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-green-100">
                <CheckIcon className="size-10 text-green-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:mt-5">
                <h3 className="mt-2 text-xl/6 text-gray-900">
                    Your data migration is complete.
                </h3>
                <div className="mt-3">
                    <p className="text-sm text-gray-500">
                        Have questions or found bugs? Want to migrate again?
                    </p>
                    <p className="mt-4 text-sm text-gray-500">
                        Email{" "}
                        <a href="mailto:support@joyapp.com" className="font-semibold">
                            support@joyapp.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export const Form = (props: Props) => {
    const [isLoading, setIsLoading] = React.useState(false);

    return (
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <form
                className="space-y-6"
                noValidate
                onSubmit={async (event) => {
                    event.preventDefault();

                    const validated = props.form.validated;
                    if (!validated.success) return;
                    const revision = props.form.revision;
                    setIsLoading(true);
                    const response = await server.journal.mfp.create_mfp_account(
                        validated.data,
                    );

                    if (response.type == "success") {
                        setIsLoading(false);
                        if (response.data.status == "VALIDATED") {
                            void analytics.track("QUEUED_MFP_MIGRATION");
                        } else if (response.data.status == "ERROR") {
                            void analytics.track("QUEUED_MFP_MIGRATION_WITH_ERRORS");
                        }
                        void props.dispatch(
                            orm.dismissAnnouncement("MIGRATE_FROM_MYFITNESSPAL"),
                        );

                        const updatedProfile = produce(props.profile, (draft) => {
                            draft.settings.myfitnesspal = {status: "ENABLED"};
                        });
                        props.dispatch({
                            type: "SET_PROFILE",
                            profile: updatedProfile,
                        });
                        props.setResolved(() => ({migrate: response.data}));
                    } else if (response.type == "invalid") {
                        void analytics.track("SUBMITTED_INVALID_MFP_CREDENTIALS");
                        props.form.setServerErrors(
                            {
                                email: ["Invalid credentials"],
                                password: [
                                    "Try again or or click below to reset your password",
                                ],
                            },
                            {revision},
                        );
                        setIsLoading(false);
                    } else {
                        setIsLoading(false);
                    }
                }}
            >
                <forms.Field field={props.form.fields.email} />
                <forms.Field field={props.form.fields.password} />

                <div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="flex w-full justify-center rounded-md bg-[#0066EE] px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid"
                    >
                        {isLoading ? "Processing..." : "Sign in"}
                    </button>
                </div>
            </form>

            <p className="mt-10 text-center text-sm text-gray-500">
                <typography.A
                    href="https://www.myfitnesspal.com/account/forgot-password"
                    external
                    track="mfp_forgot_password"
                    className="leading-6 font-semibold text-[#0066EE] hover:text-indigo-500"
                >
                    Forgot your password?
                </typography.A>
            </p>

            <p className="mt-10 text-center text-sm text-gray-500">
                Why do we need this?{" "}
                <button
                    onClick={props.why}
                    className="leading-6 font-semibold text-[#0066EE] hover:text-indigo-500"
                >
                    Learn more
                </button>
            </p>
        </div>
    );
};

type Props = React.ComponentProps<Parameters<(typeof MigrateHooks)["withIndex"]>[0]>;

export const MigrateHooks = router.Migrate.withHooks((props) => {
    const form = forms.useForm(server.journal.mfp.MFPAccountForm);
    const why = () => {
        void analytics.track("CLICKED_MFP_LEARN_MORE");
        void props.dispatch(
            dialogs.alert({
                message: "How It Works",
                body: "We use your MyFitnessPal credentials to export your data **securely**. \n\nAfter migration is complete, you can reset your credentials.\n\n",
            }),
        );
    };
    return {form, why};
});

export const Migrate = MigrateHooks.withIndex((props) => {
    const Content =
        props.resolved.migrate == null
            ? Form
            : props.resolved.migrate.status == "MIGRATED"
              ? Migrated
              : Queued;

    return (
        <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                <graphics.Mfp
                    style={{color: "#0066EE"}}
                    className="mx-auto h-10 w-auto"
                    title="MyFitnessPal"
                />
                <h2 className="mt-2 -ml-2 text-center text-2xl">Migration</h2>
            </div>
            <Content {...props} />
        </div>
    );
})
    .withActions(() => [])
    .withOptions(() => ({
        title: "Migrate From MyFitnessPal",
        titleSection: <></>,
        back: {
            title: "Journal",
            locator: router.locator({route: "base.index", params: {}}),
        },
    }));
